import type { SystemHealthService } from '@/mocks/saSystemHealthCenter';
import { statusMeta } from '@/mocks/saSystemHealthCenter';
import AvailabilityChart from './AvailabilityChart';

interface Props {
  service: SystemHealthService | null;
  onClose: () => void;
}

export default function ServiceDetailModal({ service, onClose }: Props) {
  if (!service) return null;

  const meta = statusMeta[service.status];
  const activeIncidents = service.incidents.filter((i) => !i.resolved);
  const resolvedIncidents = service.incidents.filter((i) => i.resolved);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto border border-background-200">
        {/* Header */}
        <div className="sticky top-0 bg-background-50 rounded-t-2xl border-b border-background-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-xl ${meta.bg} flex items-center justify-center`}>
              <i className={`${service.icon} ${meta.color} text-lg`}></i>
            </div>
            <div>
              <h3 className="text-foreground-900 font-bold text-sm">{service.name}</h3>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${meta.bg} ${meta.color}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}></span>
                  {meta.label}
                </span>
                <span className="text-foreground-400 text-[10px]">{service.version}</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
          >
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Description */}
          <p className="text-foreground-500 text-xs leading-relaxed">{service.description}</p>

          {/* Endpoint */}
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1">Endpoint</p>
            <code className="text-foreground-700 text-[11px] font-mono">{service.endpoint}</code>
          </div>

          {/* Metrics grid */}
          <div>
            <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-3">Métricas en tiempo real</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-background-100 rounded-xl p-3">
                <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Disponibilidad</p>
                <p className={`text-sm font-bold ${service.availability >= 99.9 ? 'text-emerald-600' : service.availability >= 99.0 ? 'text-amber-600' : 'text-red-600'}`}>
                  {service.availability.toFixed(2)}%
                </p>
              </div>
              <div className="bg-background-100 rounded-xl p-3">
                <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Respuesta</p>
                <p className={`text-sm font-bold ${service.responseTime < 100 ? 'text-emerald-600' : service.responseTime < 300 ? 'text-amber-600' : 'text-red-600'}`}>
                  {service.responseTime}ms
                </p>
              </div>
              <div className="bg-background-100 rounded-xl p-3">
                <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Última prueba</p>
                <p className="text-sm font-bold text-foreground-900">
                  {new Date(service.lastChecked).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              <div className="bg-background-100 rounded-xl p-3">
                <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Errores (24h)</p>
                <p className={`text-sm font-bold ${service.recentErrors === 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {service.recentErrors}
                </p>
              </div>
            </div>
          </div>

          {/* Availability chart */}
          <AvailabilityChart service={service} />

          {/* Incidents */}
          {service.incidents.length > 0 && (
            <div>
              <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-3">
                Historial de incidentes ({service.incidents.length})
              </p>
              <div className="space-y-3">
                {activeIncidents.length > 0 && (
                  <>
                    <p className="text-red-600 text-[10px] font-semibold flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                      Activos ({activeIncidents.length})
                    </p>
                    {activeIncidents.map((incident) => (
                      <IncidentCard key={incident.id} incident={incident} />
                    ))}
                  </>
                )}
                {resolvedIncidents.length > 0 && (
                  <>
                    {activeIncidents.length > 0 && (
                      <p className="text-emerald-600 text-[10px] font-semibold pt-2">
                        Resueltos ({resolvedIncidents.length})
                      </p>
                    )}
                    {resolvedIncidents.map((incident) => (
                      <IncidentCard key={incident.id} incident={incident} />
                    ))}
                  </>
                )}
              </div>
            </div>
          )}

          {service.incidents.length === 0 && (
            <div className="text-center py-8">
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-3">
                <i className="ri-check-line text-emerald-500 text-xl"></i>
              </div>
              <p className="text-foreground-600 text-sm font-semibold">Sin incidentes registrados</p>
              <p className="text-foreground-400 text-[11px] mt-1">Este servicio no ha reportado incidentes en los últimos 30 días.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-background-200 px-6 py-3 flex items-center justify-between">
          <span className="text-foreground-400 text-[10px]">
            Última verificación: {new Date(service.lastChecked).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-[11px] font-semibold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

function IncidentCard({ incident }: { incident: SystemHealthService['incidents'][number] }) {
  const severityColors: Record<string, { bg: string; text: string; border: string }> = {
    minor: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
    major: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
    critical: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  };

  const sc = severityColors[incident.severity] || severityColors.minor;

  return (
    <div className={`${sc.bg} border ${sc.border} rounded-xl p-4`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          {!incident.resolved && (
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0 mt-0.5"></span>
          )}
          <p className={`text-xs font-bold ${sc.text}`}>{incident.title}</p>
        </div>
        <span className="text-foreground-400 text-[9px] shrink-0 whitespace-nowrap">
          {new Date(incident.date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
      <p className="text-foreground-500 text-[11px] leading-relaxed mb-2">{incident.description}</p>
      <div className="flex items-center gap-4 text-[10px]">
        <span className="text-foreground-500">
          <span className="font-semibold">Duración:</span> {incident.duration}
        </span>
        <span className="text-foreground-500">
          <span className="font-semibold">Severidad:</span> {incident.severity === 'minor' ? 'Menor' : incident.severity === 'major' ? 'Mayor' : 'Crítico'}
        </span>
        <span className={`font-semibold ${incident.resolved ? 'text-emerald-600' : 'text-red-600'}`}>
          {incident.resolved ? 'Resuelto' : 'Activo'}
        </span>
      </div>
    </div>
  );
}